import { motion } from "motion/react";
import { Crosshair, CircleDot, Hand, Activity, SmilePlus, Briefcase } from "lucide-react";
import { siteData } from "../data/mock";

const serviceIcons = {
  1: Crosshair,
  2: CircleDot,
  3: Hand,
  4: Activity,
  5: SmilePlus,
  6: Briefcase,
};

const accentColors = {
  purple: { bg: "#F9E8FA", icon: "#987D9C" },
  blue: { bg: "#E4EDF8", icon: "#768597" },
  orange: { bg: "#FEEFDC", icon: "#BCA182" },
  pink: { bg: "#FCC9C7", icon: "#C47A77" },
  green: { bg: "#b8d1ba", icon: "#5E8C61" },
  grey: { bg: "#E9E1E1", icon: "#7A7070" },
};

export const ServicesSection = () => {
  const { services, images } = siteData;

  return (
    <section
      id="services"
      className="py-20 md:py-28 relative"
      style={{ background: "rgba(255, 244, 232, 0.35)" }}
      aria-labelledby="services-heading"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-9">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#999999] block mb-3">
              Our Services
            </span>
            <h2
              id="services-heading"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                lineHeight: 1.15,
                fontWeight: 600,
                color: "#232323",
                letterSpacing: "-0.01em",
              }}
            >
              Comprehensive Healing
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-end"
          >
            <p
              className="max-w-[440px]"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.95rem",
                lineHeight: 1.65,
                color: "#353535",
              }}
            >
              Each treatment is a personalized blend of traditional techniques
              designed to address your unique health concerns and restore balance.
            </p>
          </motion.div>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, index) => {
            const Icon = serviceIcons[service.id];
            const colors = accentColors[service.accent];

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className="rounded-xl p-6 cursor-default hover:-translate-y-1 transition-transform duration-300 flex flex-col"
                style={{
                  background: colors.bg,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                }}
                role="article"
                aria-label={service.title}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center mb-4"
                  style={{ background: "rgba(255,255,255,0.55)" }}
                >
                  <Icon size={19} strokeWidth={1.5} color={colors.icon} />
                </div>
                <h3
                  className="mb-2"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "#232323",
                  }}
                >
                  {service.title}
                </h3>
                <p
                  className="flex-1"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    color: "#353535",
                    margin: 0,
                  }}
                >
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Cupping image accent */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-14 rounded-2xl overflow-hidden relative"
          style={{ maxHeight: "320px" }}
        >
          <img
            src={images.cupping}
            alt="Cupping therapy session"
            className="w-full h-full object-cover"
            loading="lazy"
            style={{ minHeight: "240px" }}
          />
          <div
            className="absolute inset-0 flex items-end p-8"
            style={{
              background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.45) 100%)",
            }}
          >
            <div>
              <p
                className="text-white mb-1"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                  fontWeight: 600,
                }}
              >
                Traditional Cupping Therapy
              </p>
              <p
                className="text-white/80"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                Integrated into every comprehensive treatment session
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
